import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsApi } from '@/api/projects';
import { useProjectStore } from '@/stores/projectStore';
import { useToast } from '@/contexts/ToastContext';
import type { CreateProjectRequest, Project } from '@/types';

export function useProjects(page = 1, pageSize = 20) {
  const { setProjects, setLoading } = useProjectStore();

  return useQuery({
    queryKey: ['projects', page, pageSize],
    queryFn: async () => {
      setLoading(true);
      try {
        const response = await projectsApi.list(page, pageSize);
        setProjects(response.items, response.totalCount, page);
        return response;
      } finally {
        setLoading(false);
      }
    },
  });
}

export function useProject(slug: string) {
  const { setCurrentProject } = useProjectStore();

  return useQuery({
    queryKey: ['project', slug],
    queryFn: async () => {
      const project = await projectsApi.get(slug);
      setCurrentProject(project);
      return project;
    },
    enabled: !!slug,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  const { addProject } = useProjectStore();
  const toast = useToast();

  return useMutation({
    mutationFn: (data: CreateProjectRequest) => projectsApi.create(data),
    onSuccess: (project) => {
      addProject(project);
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Project Created', `"${project.name}" has been created successfully.`);
    },
    onError: (error: Error) => {
      toast.error('Failed to Create Project', error.message);
    },
  });
}

export function useUpdateProject(slug: string) {
  const queryClient = useQueryClient();
  const { updateProject } = useProjectStore();
  const toast = useToast();

  return useMutation({
    mutationFn: (data: Partial<Project>) => projectsApi.update(slug, data),
    onSuccess: (project) => {
      updateProject(project.id, project);
      queryClient.invalidateQueries({ queryKey: ['project', slug] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Project Updated', 'Your changes have been saved.');
    },
    onError: (error: Error) => {
      toast.error('Failed to Update Project', error.message);
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();
  const { removeProject } = useProjectStore();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id }: { slug: string; id: string }) =>
      projectsApi.delete(id).then(() => id),
    onSuccess: (id) => {
      removeProject(id);
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Project Deleted', 'The project has been deleted.');
    },
    onError: (error: Error) => {
      toast.error('Failed to Delete Project', error.message);
    },
  });
}

export function useProjectActions(slug: string) {
  const queryClient = useQueryClient();
  const { updateProject, currentProject } = useProjectStore();
  const toast = useToast();

  const start = useMutation({
    mutationFn: () => projectsApi.start(slug),
    onSuccess: () => {
      if (currentProject) {
        updateProject(currentProject.id, { status: 'Running' });
      }
      queryClient.invalidateQueries({ queryKey: ['project', slug] });
      toast.success('Project Started', 'Your project is now running.');
    },
    onError: (error: Error) => {
      toast.error('Failed to Start Project', error.message);
    },
  });

  const stop = useMutation({
    mutationFn: () => projectsApi.stop(slug),
    onSuccess: () => {
      if (currentProject) {
        updateProject(currentProject.id, { status: 'Stopped' });
      }
      queryClient.invalidateQueries({ queryKey: ['project', slug] });
      toast.success('Project Stopped', 'Your project has been stopped.');
    },
    onError: (error: Error) => {
      toast.error('Failed to Stop Project', error.message);
    },
  });

  const restart = useMutation({
    mutationFn: () => projectsApi.restart(slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', slug] });
      toast.success('Project Restarted', 'Your project has been restarted.');
    },
    onError: (error: Error) => {
      toast.error('Failed to Restart Project', error.message);
    },
  });

  const rebuild = useMutation({
    mutationFn: () => projectsApi.rebuild(slug),
    onSuccess: () => {
      if (currentProject) {
        updateProject(currentProject.id, { status: 'Deploying' });
      }
      queryClient.invalidateQueries({ queryKey: ['project', slug] });
      toast.success('Rebuild Started', 'Your project is being rebuilt.');
    },
    onError: (error: Error) => {
      toast.error('Failed to Rebuild Project', error.message);
    },
  });

  return { start, stop, restart, rebuild };
}
